import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import Icon from '../../Icon';

export interface ZhipsterDropdownOption {
  value: string;
  disabled?: boolean;
  object?: any;
  name?: string;
  street?: string;
  zipCode?: string;
  city?: string;
}

interface ZhipsterDropdownAttributes {
  options: ZhipsterDropdownOption[];
  onChange: (option: ZhipsterDropdownOption) => void;
  selectedValue?: any;
  className?: string;
  selectClassName?: string;
  size?: 'small' | 'large';
  placeholder?: string;
  disabled?: boolean;
  addShowAllOption?: boolean;
  fixedLabel?: boolean;
  label?: string;
}

const ZhipsterDropdown = ({
  options,
  selectedValue,
  onChange,
  className = '',
  selectClassName = '',
  size = 'large',
  placeholder,
  disabled = false,
  fixedLabel = false,
  label,
}: ZhipsterDropdownAttributes) => {
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  const selectedOption = useMemo(
    () => options?.find((o) => o.value == selectedValue),
    [selectedValue, options]
  );

  const placeholderVisible = useMemo(
    () => !!placeholder && !selectedOption,
    [placeholder, selectedOption]
  );

  const onOptionSelected = (value: string | undefined) => {
    const selectedOption = options?.find((o) => o.value == value);
    if (selectedOption) {
      onChange(selectedOption);
    }
    setDropdownOpen(false);
  };

  const selectClasses = useMemo(
    () =>
      clsx(
        selectClassName,
        'rounded flex w-full cursor-pointer appearance-none items-center border border-solid border-gray-300 text-lg focus:outline-none',
        size == 'large' ? 'p-4' : 'px-3 py-2'
      ),
    [size]
  );

  const iconClasses = useMemo(
    () =>
      clsx(
        'h-4 w-4 transition-[transform] duration-300',
        dropdownOpen ? 'rotate-180' : 'rotate-0'
      ),
    [dropdownOpen]
  );

  return (
    <>
      <div className={clsx(className, 'relative lg:hidden')}>
        <select
          className={selectClasses}
          value={selectedValue ?? placeholder}
          onChange={(e) => onOptionSelected(e.target.value)}
          onClick={() => setDropdownOpen(!dropdownOpen)}
          onBlur={() => setDropdownOpen(false)}
          disabled={disabled}
        >
          {placeholder && (
            <option disabled value={placeholder} className="text-md">
              {placeholder}
            </option>
          )}
          {options.map((option, index) => (
            <option
              className="text-md capitalize"
              disabled={option.disabled}
              value={option.value}
              key={option.value + index + 'mobile'}
            >
              ({option.name}) {option.street}, {option.zipCode}{' '}
              {option.city}
            </option>
          ))}
        </select>
        <Icon
          className={clsx(
            iconClasses,
            'absolute right-3 top-1/2 -translate-y-1/2'
          )}
          name="caret-down"
        />
        <label className="absolute -top-2 left-[14px] bg-white px-1 text-sm text-gray-700">
          {label}
        </label>
      </div>
      <div
        className={clsx(
          className,
          'relative hidden w-full text-gray-900 lg:block',
          disabled && 'pointer-events-none bg-gray-100'
        )}
        onBlur={() => setDropdownOpen(false)}
        tabIndex={1}
      >
        <div
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className={selectClasses}
        >
          <div
            className={clsx(
              'flex-1 capitalize',
              placeholderVisible && 'text-gray-400'
            )}
          >
            <div className="text-md leading-tight text-gray-700">
              {selectedOption && (
                <>
                  <div className="mb-0.5 font-bold">
                    {selectedOption.name}
                  </div>
                  <span>{`${selectedOption.street}, ${selectedOption.zipCode} ${selectedOption.city}`}</span>
                </>
              )}
            </div>
          </div>
          <Icon className={iconClasses} name="caret-down" />
        </div>
        <label className="absolute -top-2 left-[14px] bg-white px-1 text-sm text-gray-700">
          {label}
        </label>
        {dropdownOpen && (
          <ul className="dropdown-option-list absolute top-full z-10 mt-1 max-h-64 w-full overflow-y-auto rounded border border-solid border-gray-300 bg-white scrollbar-thin">
            {options.map((option, index) => (
              <li
                className={clsx(
                  'cursor-pointer border-b border-solid border-gray-300 px-3 py-2.5 text-md capitalize [&:last-child]:border-b-0',
                  option.value == selectedValue && 'font-normal',
                  (option.value == selectedValue || option.disabled) &&
                    'pointer-events-none'
                )}
                onClick={() => {
                  onOptionSelected(option.value);
                  setDropdownOpen(false);
                }}
                key={option.value + index + 'desktop'}
              >
                <div className="text-md leading-tight text-gray-700">
                  {option.name && (
                    <div className="mb-0.5 font-bold">{option.name}</div>
                  )}
                  <span>{`${option.street}, ${option.zipCode} ${option.city}`}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
};

export default ZhipsterDropdown;
